import { createRecorder, type KeystrokeLog } from '@coderaw/log'
import { countTokens, defaultEnv } from '@coderaw/runtime'
import { createBudgetBar } from '../components/editor/BudgetBar'
import { createRecordingEditor } from '../components/editor/RecordingEditor'
import { PASTE_NOTICE } from '../components/editor/recording'
import { healBrackets } from '../components/plot/heal'
import { createPlotStage } from '../components/plot/PlotStage'
import { createReplayPlayer, type ReplayPlayer } from '../components/replay/ReplayPlayer'
import { dayLight } from '../lib/clock'
import { el, replaceChildren } from '../lib/dom'
import { readSetting, writeSetting } from '../lib/storage'
import { createUnloadGuard, isDraftUnsaved } from './draftGuard'
import { EDIT_MEDIA, type Page, type PageContext } from './types'
import { createSaveFlow, type SaveFlow } from './workbenchSave'
import { buildWorkbenchView, createPhoneNotice } from './workbenchView'

const ASSISTED_KEY = 'coderaw.assisted'
const DEBOUNCE_MS = 150
const NOTICE_MS = 1800

/** A short in-page message (never alert()) that fades on its own. */
const createNotice = (): { element: HTMLElement; show: (text: string) => void } => {
  const element = el('p', { class: 'notice', role: 'status' })
  let timer: number | null = null
  return {
    element,
    show: (text) => {
      element.textContent = text
      element.classList.add('is-visible')
      if (timer !== null) clearTimeout(timer)
      timer = window.setTimeout(() => element.classList.remove('is-visible'), NOTICE_MS)
    },
  }
}

/** "watch my replay": plays the in-memory log under the editor, no network. */
const createDraftReplay = (
  host: HTMLElement,
  plotId: string,
  env: () => ReturnType<typeof defaultEnv>,
) => {
  let replay: ReplayPlayer | null = null
  return {
    play: (log: KeystrokeLog) => {
      replay?.destroy()
      replay = createReplayPlayer({ log, plotId, env })
      replaceChildren(host, [
        el('h2', { class: 'section-title' }, 'your replay, so far'),
        replay.element,
      ])
      host.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    destroy: () => replay?.destroy(),
  }
}

const assistedCheckbox = (): HTMLInputElement =>
  el('input', {
    type: 'checkbox',
    id: 'assisted',
    checked: readSetting(ASSISTED_KEY) === '1',
    onChange: (event) =>
      writeSetting(ASSISTED_KEY, (event.target as HTMLInputElement).checked ? '1' : '0'),
  })

const saveButton = (saver: SaveFlow): HTMLButtonElement => {
  const button = el('button', { class: 'btn btn-primary', type: 'button' }, 'save & share')
  button.addEventListener('click', () => {
    button.disabled = true
    void saver.save().finally(() => {
      button.disabled = false
    })
  })
  return button
}

const watchButton = (onClick: () => void): HTMLButtonElement =>
  el('button', { class: 'btn', type: 'button', onClick }, 'watch my replay')

/** The making side of the kill test: prompt, recording editor, live plot, replay, save. */
export const createWorkbench = (ctx: PageContext): Page => {
  if (!matchMedia(EDIT_MEDIA).matches) return { element: createPhoneNotice(ctx), destroy: () => {} }

  const recorder = createRecorder({ now: () => performance.now() })
  const env = () =>
    defaultEnv({ weather: ctx.prompt.weather, intensity: ctx.prompt.intensity, light: dayLight() })
  const snapshot = (): KeystrokeLog => {
    const log = recorder.snapshot()
    return { ...log, assisted: log.ops.some((op) => op.kind === 'assisted') }
  }

  const stage = createPlotStage('draft', env, 'your plot, live')
  const budget = createBudgetBar()
  const notice = createNotice()
  const status = el('p', { class: 'status', 'aria-live': 'polite' })
  const editorHost = el('div', { class: 'editor-host' })
  const replayHost = el('div', { class: 'replay-host' })
  const draftReplay = createDraftReplay(replayHost, 'draft-replay', env)
  let debounce: number | null = null

  const editor = createRecordingEditor({
    parent: editorHost,
    recorder,
    assisted: () => readSetting(ASSISTED_KEY) === '1',
    onBlockedPaste: () => notice.show(PASTE_NOTICE),
    onChange: (doc) => {
      budget.update(countTokens(doc))
      if (debounce !== null) clearTimeout(debounce)
      debounce = window.setTimeout(() => stage.runner.load(healBrackets(doc)), DEBOUNCE_MS)
    },
  })
  const saver = createSaveFlow({
    api: ctx.api,
    loggedIn: ctx.me !== null,
    prompt: ctx.prompt.text,
    doc: () => editor.doc(),
    log: snapshot,
    status,
  })
  const detachGuard = createUnloadGuard(() => isDraftUnsaved(editor.doc(), saver.savedDoc()))

  const element = buildWorkbenchView(ctx, {
    editorHost,
    notice: notice.element,
    stage: stage.element,
    budget: budget.element,
    watchButton: watchButton(() => draftReplay.play(snapshot())),
    saveButton: saveButton(saver),
    status,
    assistedBox: assistedCheckbox(),
    replayHost,
  })

  stage.runner.start()
  queueMicrotask(() => editor.focus())

  return {
    element,
    suspend: () => stage.runner.stop(),
    resume: () => {
      stage.runner.start()
      queueMicrotask(() => editor.focus())
    },
    destroy: () => {
      if (debounce !== null) clearTimeout(debounce)
      detachGuard()
      draftReplay.destroy()
      editor.destroy()
      stage.destroy()
    },
  }
}
